import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MessageCircle, Send, Search, User } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { logBulkProfileView, logProfileAccess } from "@/lib/audit-logger";

interface Profile {
  id: string;
  name: string;
  phone: string | null;
  whatsapp: string | null;
}

interface Message {
  id: string;
  user_id: string;
  body: string;
  direction: "in" | "out";
  created_at: string;
  status: string;
}

interface UserWithMessages extends Profile {
  lastMessage: Message | null;
  unreadCount: number;
  allMessages: Message[];
}

export default function AdminChat() {
  const [users, setUsers] = useState<UserWithMessages[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithMessages[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserWithMessages | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin");

    if (!roles || roles.length === 0) {
      navigate("/today");
      return;
    }

    fetchUsersWithMessages();
  };

  const fetchUsersWithMessages = async () => {
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, name, phone, whatsapp");

      if (profilesError) throw profilesError;

      // Fetch all messages
      const { data: messages, error: messagesError } = await supabase
        .from("messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (messagesError) throw messagesError;

      // Log bulk profile view for audit (chat list access)
      if (profiles && profiles.length > 0) {
        logBulkProfileView(profiles.map(p => p.id));
      }

      // Group messages by user and calculate stats
      const usersWithMessages: UserWithMessages[] = profiles
        .map((profile) => {
          const userMessages = messages.filter((m) => m.user_id === profile.id);
          const lastMessage = userMessages[0] || null;
          const unreadCount = userMessages.filter(
            (m) => m.direction === "in" && m.status === "sent"
          ).length;

          return {
            ...profile,
            lastMessage,
            unreadCount,
            allMessages: userMessages.reverse(),
          };
        })
        .filter((u) => u.allMessages.length > 0)
        .sort((a, b) => {
          if (!a.lastMessage || !b.lastMessage) return 0;
          return new Date(b.lastMessage.created_at).getTime() - new Date(a.lastMessage.created_at).getTime();
        });

      setUsers(usersWithMessages);
      setFilteredUsers(usersWithMessages);
    } catch (error: any) {
      console.error("Error fetching messages:", error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query) ||
        user.whatsapp?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // Real-time subscription for new messages (admin side)
  useEffect(() => {
    const channel = supabase
      .channel('admin-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          console.log('New message received (admin):', payload);
          const newMessage = payload.new as Message;
          
          // Refresh the users list to update unread counts and last message
          fetchUsersWithMessages();
          
          // If the message is from the currently selected user, update their conversation
          if (selectedUser && newMessage.user_id === selectedUser.id) {
            setSelectedUser((current) => {
              if (!current) return current;
              return {
                ...current,
                allMessages: [...current.allMessages, newMessage],
                lastMessage: newMessage,
                unreadCount: newMessage.direction === 'in' ? current.unreadCount + 1 : current.unreadCount
              };
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedUser]);

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedUser) return;

    setSending(true);
    try {
      const { error } = await supabase.from("messages").insert({
        user_id: selectedUser.id,
        body: replyText.trim(),
        direction: "out",
        status: "sent",
      });

      if (error) throw error;

      setReplyText("");
      await fetchUsersWithMessages();
      
      // Update selected user
      const updatedUser = users.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }

      toast({
        title: "Reply sent",
        description: "Your message has been sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">લોડ થઈ રહ્યું છે...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))]">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="દમયંતી બાગ" className="h-10" />
              <h1 className="text-xl font-bold text-primary">Chat Queries</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="mr-2 w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-h-[calc(100vh-12rem)]">
          {/* Users List */}
          <MbCard className="lg:col-span-1 flex flex-col overflow-hidden">
            <div className="p-4 border-b flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{searchQuery ? "No users found" : "No messages yet"}</p>
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        // Log individual chat view for audit
                        logProfileAccess({ 
                          profileId: user.id, 
                          accessType: 'view' 
                        });
                      }}
                      className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${
                        selectedUser?.id === user.id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span className="font-medium">{user.name}</span>
                          </div>
                          {user.phone && (
                            <p className="text-xs opacity-70 mt-1">📱 {user.phone}</p>
                          )}
                          {user.lastMessage && (
                            <p className="text-xs opacity-70 mt-1 truncate">
                              {user.lastMessage.direction === "in" ? "↓ " : "↑ "}
                              {user.lastMessage.body}
                            </p>
                          )}
                        </div>
                        {user.unreadCount > 0 && (
                          <Badge className="bg-red-600 text-white ml-2">
                            {user.unreadCount}
                          </Badge>
                        )}
                      </div>
                      {user.lastMessage && (
                        <p className="text-xs opacity-50 mt-2">
                          {formatDistanceToNow(new Date(user.lastMessage.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </MbCard>

          {/* Conversation View */}
          <MbCard className="lg:col-span-2 flex flex-col overflow-hidden">
            {selectedUser ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 border-b flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <User className="w-8 h-8 text-muted-foreground" />
                    <div>
                      <h2 className="font-bold">{selectedUser.name}</h2>
                      {selectedUser.phone && (
                        <p className="text-sm text-muted-foreground">
                          📱 {selectedUser.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4 min-h-full">
                    {selectedUser.allMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-center py-8 text-muted-foreground">
                        <div>
                          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No messages yet</p>
                        </div>
                      </div>
                    ) : (
                      selectedUser.allMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.direction === "out" ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.direction === "out"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                          <p
                            className={`text-xs mt-1 ${
                              message.direction === "out"
                                ? "text-primary-foreground/70"
                                : "text-muted-foreground"
                            }`}
                          >
                            {new Date(message.created_at).toLocaleTimeString("en-IN", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    ))
                    )}
                  </div>
                </ScrollArea>

                {/* Reply Input */}
                <form onSubmit={handleSendReply} className="p-4 border-t flex-shrink-0">
                  <div className="flex gap-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      disabled={sending}
                      className="flex-1 min-h-[100px] max-h-[200px] resize-none"
                      rows={3}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply(e);
                        }
                      }}
                    />
                    <Button
                      type="submit"
                      variant="default"
                      size="icon"
                      disabled={sending || !replyText.trim()}
                      className="self-end"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Press Enter to send, Shift+Enter for new line
                  </p>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No Conversation Selected</h3>
                  <p className="text-muted-foreground">
                    Select a user from the list to view their messages
                  </p>
                </div>
              </div>
            )}
          </MbCard>
        </div>
      </div>
    </div>
  );
}
